import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import {
  generateGladeCourseFirstCertificate,
  generateGladeCourseSecondCertificate,
  getGladeStudentDetail,
  updateGladeStudentDetail,
  uploadGladeManualCertificate,
} from "../../api/server";
import DashBoardSidebar from "../../layouts/dashBoard-Sidebar";
import DashboardNavbar from "../../layouts/dashboardNavbar";

const emptyValue = "—";

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return emptyValue;
  return String(value);
};

const formatDate = (value) => {
  if (!value) return emptyValue;
  const dateMatch = String(value).match(/^\d{4}-\d{2}-\d{2}/);
  if (dateMatch) return dateMatch[0];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatValue(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatPercentage = (value) => {
  const percentage = Number(value);
  return Number.isFinite(percentage) ? Math.round(percentage) : 0;
};

const isSafeUrl = (value) =>
  typeof value === "string" && /^https?:\/\//i.test(value.trim());

const normalizeCourseAccessUrl = (value) => {
  if (typeof value !== "string" || !value.trim()) return "";

  let candidate = value.trim();
  if (/^(undefined|null)\//i.test(candidate)) {
    candidate = candidate.replace(/^(undefined|null)/i, window.location.origin);
  } else if (/^\/?course(?:1|2)\//i.test(candidate)) {
    candidate = new URL(candidate.replace(/^\//, ""), `${window.location.origin}/`).href;
  }

  if (!isSafeUrl(candidate)) return "";

  try {
    const url = new URL(candidate);
    if (!url.searchParams.has("params") && url.search.length > 1) {
      const encodedParams = url.search.slice(1);
      url.search = "";
      url.searchParams.set("params", encodedParams);
    }
    return url.href;
  } catch (error) {
    return "";
  }
};

const ReadOnlyField = ({
  label,
  value,
  columnClass = "col-lg-6",
  emptyDisplay = emptyValue,
}) => {
  const displayValue =
    value === null || value === undefined || value === ""
      ? emptyDisplay
      : String(value);

  return (
    <div className={columnClass}>
      <div className="input_wrap glade-readonly-field">
        <label className="label-title">{label}</label>
        <input className="form-control" type="text" readOnly value={displayValue} />
      </div>
    </div>
  );
};

const EditableField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  columnClass = "col-lg-6",
  ...inputProps
}) => (
  <div className={columnClass}>
    <div className="input_wrap glade-editable-field">
      <label className="label-title" htmlFor={`glade-${name}`}>
        {label}
      </label>
      <input
        className="form-control"
        id={`glade-${name}`}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        {...inputProps}
      />
    </div>
  </div>
);

const createFormValues = (studentDetails) => {
  const currentEnrollment = studentDetails?.enrollment || {};
  const courses = studentDetails?.courseEnrollments || [];
  const creditCounseling = courses.find(
    (course) => String(course.courseType).toUpperCase() === "CC"
  );
  const debtorEducation = courses.find(
    (course) => String(course.courseType).toUpperCase() === "DE"
  );

  return {
    notes: currentEnrollment.notes ?? "",
    bankruptcyCaseNumber:
      creditCounseling?.caseNumber || debtorEducation?.caseNumber || "",
    firstName: currentEnrollment.firstName || "",
    lastName: currentEnrollment.lastName || "",
    ssnLast4: currentEnrollment.ssnLast4 || "",
    birthDate: currentEnrollment.birthDate
      ? formatDate(currentEnrollment.birthDate)
      : "",
    phone: currentEnrollment.phone || "",
    email: currentEnrollment.email || "",
    address: currentEnrollment.address || "",
    city: currentEnrollment.city || "",
    state: currentEnrollment.state || "",
    zip: currentEnrollment.zip || "",
    spouseFirstName: currentEnrollment.spouseFirstName || "",
    spouseLastName: currentEnrollment.spouseLastName || "",
    spouseSsn: currentEnrollment.spouseSsn || "",
  };
};

const Section = ({ title, children, className = "" }) => (
  <div className={`row glade-detail-section ${className}`.trim()}>
    <div className="client-head">
      <div className="row align-items-center">
        <div className="col-lg-12">
          <h1>{title}</h1>
        </div>
      </div>
    </div>
    {children}
  </div>
);

const FilerProgress = ({
  name,
  role,
  percentage,
  enrolled,
  certificateUrl,
  courseCode,
  filerType,
  onGenerateCertificate,
  generatingType,
  onUploadCertificate,
  uploadingType,
}) => {
  const completedPercentage = enrolled ? formatPercentage(percentage) : 0;
  const hasSafeCertificateUrl = isSafeUrl(certificateUrl);
  const generationKey = `${courseCode}-${filerType}`;
  const isGenerating = generatingType === generationKey;
  const isUploading = uploadingType === generationKey;
  const certificateActionInProgress = Boolean(generatingType || uploadingType);

  return (
    <div className="glade-filer-progress">
      <div className="glade-filer-progress-heading">
        <div>
          <strong>{formatValue(name)}</strong>
          <span>{role}</span>
        </div>
        <strong className="glade-progress-value">
          {enrolled ? `${completedPercentage}%` : "Not Enrolled"}
        </strong>
      </div>
      <div
        className="glade-progress-track"
        role="progressbar"
        aria-label={`${name} ${role} course progress`}
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={completedPercentage}
      >
        <span style={{ width: `${completedPercentage}%` }}></span>
      </div>

      {enrolled && (
        <div className="glade-filer-certificate-action">
          <span>Certificate</span>
          <div className="glade-certificate-buttons">
            {hasSafeCertificateUrl ? (
              <a href={certificateUrl} target="_blank" rel="noreferrer">
                <i className="fa-regular fa-eye" aria-hidden="true"></i>
                View
              </a>
            ) : (
              <button
                type="button"
                className="glade-generate-certificate"
                disabled={certificateActionInProgress}
                onClick={() => onGenerateCertificate(courseCode, filerType)}
                title={`Generate ${filerType} ${courseCode} certificate`}
              >
                {isGenerating ? "Generating..." : "Generate"}
              </button>
            )}
            <label
              className={`glade-manual-certificate-upload${
                certificateActionInProgress ? " is-disabled" : ""
              }`}
              title={`Upload ${filerType} ${courseCode} certificate PDF`}
            >
              <input
                type="file"
                accept="application/pdf,.pdf"
                disabled={certificateActionInProgress}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) {
                    onUploadCertificate(courseCode, filerType, file);
                  }
                }}
              />
              <i className="fa-solid fa-upload" aria-hidden="true"></i>
              {isUploading ? "Uploading..." : "Upload PDF"}
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

const CourseAccessLink = ({ label, url }) => {
  const accessUrl = normalizeCourseAccessUrl(url);
  if (!accessUrl) return null;

  return (
    <a
      className="glade-course-access-link"
      href={accessUrl}
      target="_blank"
      rel="noreferrer"
      title={accessUrl}
    >
      <span className="glade-course-access-link-content">
        <strong>{label}</strong>
        <small>{accessUrl}</small>
      </span>
      <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
    </a>
  );
};

const CourseAccessCard = ({ title, courseCode, enrollment, showJoint }) => {
  const mainCourseUrl = normalizeCourseAccessUrl(enrollment?.courseUrl);
  const spouseCourseUrl = normalizeCourseAccessUrl(enrollment?.spouseCourseUrl);
  const hasCourseUrl = Boolean(mainCourseUrl);
  const hasSpouseCourseUrl = showJoint && Boolean(spouseCourseUrl);

  if (!hasCourseUrl && !hasSpouseCourseUrl) return null;

  return (
    <div className="col-lg-6">
      <div className="glade-course-access-card">
        <div className="glade-course-access-card-header">
          <div>
            <span className="glade-course-access-code">{courseCode}</span>
            <h2>{title}</h2>
          </div>
        </div>
        {hasCourseUrl && (
          <CourseAccessLink label="Main Filer Course" url={mainCourseUrl} />
        )}
        {hasSpouseCourseUrl && (
          <CourseAccessLink
            label="Joint Filer Course"
            url={spouseCourseUrl}
          />
        )}
      </div>
    </div>
  );
};

const CourseSummaryCard = ({
  title,
  enrollment,
  progress,
  showJoint,
  mainFilerName,
  jointFilerName,
  courseCode,
  onGenerateCertificate,
  generatingType,
  onUploadCertificate,
  uploadingType,
  budgetSummaryUrl,
  progressUrl,
}) => {
  const mainProgress = progress.find(
    (record) => String(record.filerType).toLowerCase() === "main"
  );
  const jointProgress = progress.find(
    (record) => String(record.filerType).toLowerCase() === "joint"
  );
  const hasEnrollmentDate = Boolean(enrollment?.enrollmentDate);
  const mainCertificateUrl =
    mainProgress?.certificateUrl || enrollment?.certificateUrl;

  return (
    <div className="col-lg-6">
      <div className="glade-course-card">
        <div className="glade-course-card-header">
          <h2>{title}</h2>
          <div className="glade-course-card-header-actions">
            {enrollment && progressUrl && (
              <Link
                className="glade-course-progress-action"
                to={progressUrl}
                title={`Edit ${title} progress`}
              >
                <i className="fa-regular fa-pen-to-square" aria-hidden="true"></i>
                Edit progress
              </Link>
            )}
            <span className={enrollment ? "is-enrolled" : "not-enrolled"}>
              {enrollment ? "Enrolled" : "Not Enrolled"}
            </span>
          </div>
        </div>

        <FilerProgress
          name={mainFilerName}
          role="Main Filer"
          percentage={mainProgress?.percentage}
          enrolled={Boolean(enrollment)}
          certificateUrl={mainCertificateUrl}
          courseCode={courseCode}
          filerType="Main"
          onGenerateCertificate={onGenerateCertificate}
          generatingType={generatingType}
          onUploadCertificate={onUploadCertificate}
          uploadingType={uploadingType}
        />

        {showJoint && (
          <FilerProgress
            name={jointFilerName}
            role="Joint Filer"
            percentage={jointProgress?.percentage}
            enrolled={Boolean(enrollment?.spouseEnrollmentDate || jointProgress)}
            certificateUrl={jointProgress?.certificateUrl}
            courseCode={courseCode}
            filerType="Joint"
            onGenerateCertificate={onGenerateCertificate}
            generatingType={generatingType}
            onUploadCertificate={onUploadCertificate}
            uploadingType={uploadingType}
          />
        )}

        {hasEnrollmentDate && (
          <div className="glade-course-summary-row">
            <span>Enrollment Date</span>
            <strong>{formatDate(enrollment.enrollmentDate)}</strong>
          </div>
        )}

        {courseCode === "CC" && budgetSummaryUrl && (
          <div className="glade-course-summary-row">
            <span>Budget Analysis</span>
            <Link className="glade-budget-summary-link" to={budgetSummaryUrl}>
              <i className="fa-regular fa-eye" aria-hidden="true"></i>
              View Summary
            </Link>
          </div>
        )}

      </div>
    </div>
  );
};

const GladeClientDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const clientId = new URLSearchParams(location.search).get("id");
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [generatingCourse, setGeneratingCourse] = useState("");
  const [uploadingCourse, setUploadingCourse] = useState("");
  const [formValues, setFormValues] = useState(() => createFormValues());
  const [saving, setSaving] = useState(false);

  const fetchDetails = useCallback(async () => {
    if (!clientId) {
      setError("Glade client ID is missing");
      setLoading(false);
      return;
    }

    try {
      const response = await getGladeStudentDetail(clientId);
      const studentDetails = response.data.result;
      setDetails(studentDetails);
      setFormValues(createFormValues(studentDetails));
      setError("");
    } catch (requestError) {
      const message =
        requestError.response?.data?.error || "Unable to load Glade client details";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const enrollment = details?.enrollment;

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));
  };

  const handleUpdate = async (event) => {
    event.preventDefault();
    if (!clientId || saving) return;

    setSaving(true);
    try {
      const response = await updateGladeStudentDetail(clientId, formValues);
      const updatedDetails = response.data?.result;
      if (updatedDetails) {
        setDetails(updatedDetails);
        setFormValues(createFormValues(updatedDetails));
      }
      toast.success(
        response.data?.message || "Glade client details updated successfully"
      );
    } catch (requestError) {
      const message =
        requestError.response?.data?.error ||
        requestError.response?.data?.message ||
        "Unable to update Glade client details";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateCertificate = async (courseCode, filerType) => {
    if (!enrollment?.clientId || generatingCourse || uploadingCourse) return;

    const generationKey = `${courseCode}-${filerType}`;
    const isJoint = filerType === "Joint";
    setGeneratingCourse(generationKey);
    try {
      const generate =
        courseCode === "CC"
          ? generateGladeCourseFirstCertificate
          : generateGladeCourseSecondCertificate;
      const response = await generate(enrollment.clientId, isJoint);
      toast.success(
        response.data?.message ||
          `${filerType} ${courseCode} certificate generated successfully`
      );
      await fetchDetails();
      if (courseCode === "DE" && enrollment.sendEmail && enrollment.courtLink) {
        navigate(`/certificate-to-court?source=glade&id=${encodeURIComponent(clientId)}`);
      }
    } catch (requestError) {
      const message =
        requestError.response?.data?.error ||
        requestError.response?.data?.message ||
        `Unable to generate ${filerType} ${courseCode} certificate`;
      toast.error(message);
    } finally {
      setGeneratingCourse("");
    }
  };

  const handleUploadCertificate = async (courseCode, filerType, file) => {
    if (!enrollment?.clientId || generatingCourse || uploadingCourse) return;

    const isPdf = file?.type === "application/pdf" || /\.pdf$/i.test(file?.name || "");
    if (!isPdf) {
      toast.error("Please select a PDF certificate");
      return;
    }

    const uploadKey = `${courseCode}-${filerType}`;
    setUploadingCourse(uploadKey);
    try {
      const response = await uploadGladeManualCertificate(
        enrollment.clientId,
        courseCode,
        filerType === "Joint",
        file
      );
      toast.success(
        response.data?.message ||
          `${filerType} ${courseCode} certificate uploaded successfully`
      );
      await fetchDetails();
    } catch (requestError) {
      const message =
        requestError.response?.data?.error ||
        requestError.response?.data?.message ||
        `Unable to upload ${filerType} ${courseCode} certificate`;
      toast.error(message);
    } finally {
      setUploadingCourse("");
    }
  };

  const creditCounseling = details?.courseEnrollments?.find(
    (course) => String(course.courseType).toUpperCase() === "CC"
  );
  const debtorEducation = details?.courseEnrollments?.find(
    (course) => String(course.courseType).toUpperCase() === "DE"
  );
  const hasSpouse = Boolean(
    enrollment?.spouseFirstName ||
      enrollment?.spouseLastName ||
      enrollment?.spouseSsn
  );
  const mainFilerName = `${enrollment?.firstName || ""} ${
    enrollment?.lastName || ""
  }`.trim();
  const jointFilerName = `${enrollment?.spouseFirstName || ""} ${
    enrollment?.spouseLastName || ""
  }`.trim();
  const hasBudgetEligibleProgress = (details?.firstCourses || []).some(
    (course) => {
      const filerType = String(course.filerType || "").toLowerCase();
      const percentage = Number.parseFloat(course.percentage);
      return (
        (filerType === "main" || filerType === "joint") &&
        Number.isFinite(percentage) &&
        percentage > 36
      );
    }
  );

  return (
    <div>
      <div className="wrapper">
        <DashBoardSidebar />
        <div id="content">
          <DashboardNavbar />
          {loading ? (
            <ThreeDots
              height="40"
              width="80"
              radius="9"
              color="#4fa94d"
              ariaLabel="three-dots-loading"
              wrapperStyle={{ display: "flex", justifyContent: "center" }}
              wrapperClassName="spinner"
              visible
            />
          ) : error || !enrollment ? (
            <section className="client-editing glade-client-details">
              <div className="glade-detail-toolbar">
                <h1>Glade Client Details</h1>
                <Link className="btn_brnd-outline" to="/managestudents?client=glade">
                  Back to Manage Students
                </Link>
              </div>
              <div className="glade-empty-state">{error || "Glade client not found"}</div>
            </section>
          ) : (
            <section className="client-editing glade-client-details">
              <div className="glade-detail-toolbar">
                <div>
                  <div className="glade-client-title">
                    <h1>
                      {enrollment.firstName} {enrollment.lastName}
                    </h1>
                    <span className="glade-client-badge">Glade</span>
                  </div>
                </div>
                <Link className="btn_brnd-outline" to="/managestudents?client=glade">
                  Back to Manage Students
                </Link>
              </div>

              <form onSubmit={handleUpdate}>
              <div className="input_wrap glade-editable-field">
                <label className="label-title" htmlFor="glade-notes">
                  Notes
                </label>
                <textarea
                  className="form-control"
                  id="glade-notes"
                  name="notes"
                  rows={4}
                  value={formValues.notes}
                  onChange={handleFieldChange}
                />
              </div>
              <Section title="Account Details">
                <EditableField
                  label="Bankruptcy Case No."
                  name="bankruptcyCaseNumber"
                  value={formValues.bankruptcyCaseNumber}
                  onChange={handleFieldChange}
                  placeholder="05-12345"
                  pattern="[0-9]{2}-[0-9]{5}"
                  title="Use the format 05-12345"
                  maxLength="8"
                />
                <ReadOnlyField label="Attorney Name" value={enrollment.attorneyName} />
                <ReadOnlyField label="Attorney Code" value={enrollment.attorneyCode} />
                <ReadOnlyField label="Judicial District" value={enrollment.districtName} />
                <ReadOnlyField
                  label="Course Delivery Method"
                  value={enrollment.courseTypeDetail}
                />
              </Section>

              <Section title="Personal Info...">
                <EditableField
                  label="First Name"
                  name="firstName"
                  value={formValues.firstName}
                  onChange={handleFieldChange}
                  maxLength="100"
                  required
                />
                <EditableField
                  label="Last Name"
                  name="lastName"
                  value={formValues.lastName}
                  onChange={handleFieldChange}
                  maxLength="100"
                  required
                />
                <EditableField
                  label="Last 4 SSN"
                  name="ssnLast4"
                  value={formValues.ssnLast4}
                  onChange={handleFieldChange}
                  inputMode="numeric"
                  pattern="[0-9]{4}"
                  maxLength="4"
                  required
                />
                <EditableField
                  label="Date of Birth"
                  name="birthDate"
                  value={formValues.birthDate}
                  onChange={handleFieldChange}
                  type="date"
                  required
                />
                <EditableField
                  label="Phone No."
                  name="phone"
                  value={formValues.phone}
                  onChange={handleFieldChange}
                  type="tel"
                  maxLength="20"
                  required
                />
                <EditableField
                  label="Email"
                  name="email"
                  value={formValues.email}
                  onChange={handleFieldChange}
                  type="email"
                  maxLength="500"
                  required
                />
                <ReadOnlyField label="Joint Filer" value={hasSpouse ? "Yes" : "No"} />
              </Section>

              {hasSpouse && (
                <Section title="Joint Filer Info">
                  <EditableField
                    label="First Name"
                    name="spouseFirstName"
                    value={formValues.spouseFirstName}
                    onChange={handleFieldChange}
                    maxLength="100"
                    required
                  />
                  <EditableField
                    label="Last Name"
                    name="spouseLastName"
                    value={formValues.spouseLastName}
                    onChange={handleFieldChange}
                    maxLength="100"
                    required
                  />
                  <EditableField
                    label="Spouse SSN"
                    name="spouseSsn"
                    value={formValues.spouseSsn}
                    onChange={handleFieldChange}
                    inputMode="numeric"
                    maxLength="5"
                    required
                  />
                </Section>
              )}

              <Section title="Course Details" className="glade-course-section">
                <CourseSummaryCard
                  title="Credit Counseling"
                  enrollment={creditCounseling}
                  progress={details.firstCourses || []}
                  showJoint={hasSpouse}
                  mainFilerName={mainFilerName}
                  jointFilerName={jointFilerName || "Joint Filer"}
                  courseCode="CC"
                  onGenerateCertificate={handleGenerateCertificate}
                  generatingType={generatingCourse}
                  onUploadCertificate={handleUploadCertificate}
                  uploadingType={uploadingCourse}
                  budgetSummaryUrl={
                    hasBudgetEligibleProgress
                      ? `/glade-budget-analysis-report?id=${encodeURIComponent(enrollment.id)}`
                      : ""
                  }
                  progressUrl={
                    creditCounseling
                      ? `/glade-course-progress?id=${encodeURIComponent(
                          enrollment.id
                        )}&course=CC`
                      : ""
                  }
                />
                <CourseSummaryCard
                  title="Debtor Education"
                  enrollment={debtorEducation}
                  progress={details.secondCourses || []}
                  showJoint={hasSpouse}
                  mainFilerName={mainFilerName}
                  jointFilerName={jointFilerName || "Joint Filer"}
                  courseCode="DE"
                  onGenerateCertificate={handleGenerateCertificate}
                  generatingType={generatingCourse}
                  onUploadCertificate={handleUploadCertificate}
                  uploadingType={uploadingCourse}
                  progressUrl={
                    debtorEducation
                      ? `/glade-course-progress?id=${encodeURIComponent(
                          enrollment.id
                        )}&course=DE`
                      : ""
                  }
                />
              </Section>

              {(normalizeCourseAccessUrl(creditCounseling?.courseUrl) ||
                normalizeCourseAccessUrl(debtorEducation?.courseUrl) ||
                (hasSpouse &&
                  (normalizeCourseAccessUrl(creditCounseling?.spouseCourseUrl) ||
                    normalizeCourseAccessUrl(debtorEducation?.spouseCourseUrl)))) && (
                <Section title="Course Access Links" className="glade-course-access-section">
                  <CourseAccessCard
                    title="Credit Counseling"
                    courseCode="CC"
                    enrollment={creditCounseling}
                    showJoint={hasSpouse}
                  />
                  <CourseAccessCard
                    title="Debtor Education"
                    courseCode="DE"
                    enrollment={debtorEducation}
                    showJoint={hasSpouse}
                  />
                </Section>
              )}

              <Section title="Address">
                <EditableField
                  label="Address Line 1"
                  name="address"
                  value={formValues.address}
                  onChange={handleFieldChange}
                  columnClass="col-lg-12"
                  maxLength="255"
                  required
                />
                <EditableField
                  label="City"
                  name="city"
                  value={formValues.city}
                  onChange={handleFieldChange}
                  maxLength="100"
                  required
                />
                <EditableField
                  label="State"
                  name="state"
                  value={formValues.state}
                  onChange={handleFieldChange}
                  maxLength="2"
                  required
                />
                <EditableField
                  label="Zip Code"
                  name="zip"
                  value={formValues.zip}
                  onChange={handleFieldChange}
                  maxLength="10"
                  required
                />
              </Section>

              <div className="glade-update-actions">
                <button className="btn_brnd" type="submit" disabled={saving}>
                  {saving ? "Updating..." : "Update"}
                </button>
              </div>
              </form>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default GladeClientDetails;
